import { createUserWithEmailAndPassword, deleteUser, signOut, updateProfile } from "firebase/auth";
import { deleteDoc, doc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { db, getIsolatedFirebase } from "../firebase";
import { logAdminAction } from "./adminLog";
import { collectionHasDoc, generateUniqueSlug, getAbsoluteUrl, isCpfTaken, maskCPF, onlyDigits } from "./functions";

export interface IAdminCreateUserParams {
    name: string;
    email: string;
    phoneNumber: string;
    password: string;
    advertiser: boolean;
    cpf?: string;
    instagram?: string;
    site?: string;
    description?: string;
    actor: { id: string | null, name: string | null, email: string | null };
}

export type AdminCreateUserResult =
    | { success: true, uid: string, slug: string | null }
    | {
        success: false,
        // email-in-use: já existe conta no Auth ou documento em users com esse e-mail
        // cpf-taken: outro usuário já usa o CPF
        // check-failed: não foi possível confirmar a unicidade do CPF (fail closed)
        // auth: o Firebase Auth recusou a criação (ver errorCode)
        // write: a conta foi criada no Auth mas a gravação no Firestore falhou
        reason: "email-in-use" | "cpf-taken" | "check-failed" | "auth" | "write",
        errorCode?: string | null,
        // só em "write": se a conta no Auth e o documento em users foram apagados de volta
        rolledBack?: boolean
    };

// Cria a conta de outro usuário a partir do painel admin, sem derrubar a sessão do admin.
//
// A conta nasce numa instância isolada do Firebase (getIsolatedFirebase), e as escritas
// seguem os mesmos caminhos que o app já usa hoje, para caber nas regras do Firestore:
//   1. como o próprio usuário novo: o documento base (igual ao signUp) e os dados de perfil
//      (CPF, Instagram, site, descrição — como updateUserCpf/updateUserContact);
//   2. como o admin logado: role de anunciante + slug e as validações de e-mail/telefone
//      (como findUser.tsx).
// Se algo falhar depois da conta existir no Auth, ela é apagada (best effort) para não deixar
// o e-mail preso numa conta sem documento.
export async function adminCreateUser(params: IAdminCreateUserParams): Promise<AdminCreateUserResult> {
    const name = params.name.trim();
    const email = params.email.trim().toLowerCase();
    const phoneNumber = onlyDigits(params.phoneNumber);
    const cpf = params.cpf ? maskCPF(params.cpf) : "";

    // pré-checagens como admin, antes de criar qualquer coisa. A do e-mail pega documentos
    // sem conta no Auth; se a consulta falhar, segue — o Auth é quem garante a unicidade.
    if (await collectionHasDoc("users", "email", email)) {
        return { success: false, reason: "email-in-use" };
    }
    if (cpf) {
        const taken = await isCpfTaken(cpf);
        if (taken === undefined) {
            return { success: false, reason: "check-failed" };
        }
        if (taken) {
            return { success: false, reason: "cpf-taken" };
        }
    }

    const isolated = getIsolatedFirebase();

    let credential;
    try {
        credential = await createUserWithEmailAndPassword(isolated.auth, email, params.password);
    } catch (error: any) {
        console.log(error);
        if (error?.code === "auth/email-already-in-use") {
            return { success: false, reason: "email-in-use" };
        }
        return { success: false, reason: "auth", errorCode: error?.code || null };
    }

    const uid = credential.user.uid;
    let docCreated = false;

    try {
        await updateProfile(credential.user, { displayName: name });

        // 1. como o usuário novo — mesmo documento que o cadastro normal (signUp) grava
        await setDoc(doc(isolated.db, "users", uid), {
            name: name,
            email: email,
            phoneNumber: phoneNumber,
            roles: ["customer"],
            createdAt: serverTimestamp()
        });
        docCreated = true;

        const profileFields: { [key: string]: string } = {};
        if (cpf) {
            profileFields.cpf = cpf;
        }
        if (params.advertiser) {
            if (params.instagram?.trim()) {
                profileFields.instagram = params.instagram.trim();
            }
            if (params.site?.trim()) {
                profileFields.site = getAbsoluteUrl(params.site.trim());
            }
            if (params.description?.trim()) {
                profileFields.description = params.description.trim();
            }
        }
        if (Object.keys(profileFields).length) {
            await updateDoc(doc(isolated.db, "users", uid), profileFields);
        }

        // 2. como o admin — o que só um admin pode atestar/conceder
        const roles = params.advertiser ? ["customer", "advertiser"] : ["customer"];
        const slug = params.advertiser ? await generateUniqueSlug() : null;
        const adminFields: { [key: string]: any } = {
            emailVerified: true,
            phoneVerified: true
        };
        if (params.advertiser) {
            adminFields.roles = roles;
            adminFields.slug = slug;
        }
        await updateDoc(doc(db, "users", uid), adminFields);

        await logAdminAction({
            action: "user.create",
            actorId: params.actor.id,
            actorName: params.actor.name,
            actorEmail: params.actor.email,
            targetId: uid,
            targetName: name,
            targetEmail: email,
            details: `Conta criada pelo admin | Roles: [${roles.join(", ")}]`
                + ` | E-mail e telefone ${phoneNumber} marcados como verificados`
                + (cpf ? " | CPF preenchido" : "")
                + (slug ? ` | slug gerado: ${slug}` : "")
        });

        return { success: true, uid, slug };
    } catch (error) {
        console.log(error);
        // desfaz o que foi criado: um documento órfão bloquearia uma nova tentativa com o
        // mesmo e-mail (pré-checagem acima), e uma conta órfã no Auth também
        let docRemoved = !docCreated;
        let authRemoved = false;
        if (docCreated) {
            try {
                await deleteDoc(doc(isolated.db, "users", uid));
                docRemoved = true;
            } catch (rollbackError) {
                console.log(rollbackError);
            }
        }
        try {
            await deleteUser(credential.user);
            authRemoved = true;
        } catch (rollbackError) {
            console.log(rollbackError);
        }
        return { success: false, reason: "write", rolledBack: docRemoved && authRemoved };
    } finally {
        await signOut(isolated.auth).catch(e => console.log(e));
    }
}
